import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react'
import './header-actions-menu.css'

export interface HeaderMenuAction {
  label: string
  icon?: ReactNode
  checked?: boolean
  href?: string
  dividerBefore?: boolean
  onSelect?: () => void
}

export function HeaderActionsMenu({ actions }: { actions: HeaderMenuAction[] }) {
  const [open, setOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const closeFromPointer = (event: PointerEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const closeFromKeyboard = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', closeFromPointer)
    document.addEventListener('keydown', closeFromKeyboard)
    return () => {
      document.removeEventListener('pointerdown', closeFromPointer)
      document.removeEventListener('keydown', closeFromKeyboard)
    }
  }, [open])

  return (
    <div className="header-actions-menu" ref={menuRef}>
      <button
        className="header-actions-menu__trigger"
        type="button"
        aria-label="Open menu"
        title="Menu"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((current) => !current)}
      >
        <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
          <path d="M3 6h18M3 12h18M3 18h18" />
        </svg>
      </button>
      {open ? (
        <div className="header-actions-menu__popover" role="menu">
          {actions.map((action) => {
            const content = (
              <>
                {action.icon ? <span className="header-actions-menu__icon" aria-hidden="true">{action.icon}</span> : action.href ? (
                  <span className="header-actions-menu__icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" focusable="false"><path d="M7 3.5h7l3 3V20.5H7zM14 3.5v4h4M10 12h4M10 15.5h4" /></svg>
                  </span>
                ) : null}
                <span>{action.label}</span>
                {action.checked ? <small aria-hidden="true">On</small> : null}
              </>
            )
            return (
              <Fragment key={action.label}>
                {action.dividerBefore ? <span className="header-actions-menu__divider" role="separator" /> : null}
                {action.href ? (
                  <a role="menuitem" href={action.href} onClick={() => setOpen(false)}>{content}</a>
                ) : (
                  <button
                    type="button"
                    role={action.checked === undefined ? 'menuitem' : 'menuitemcheckbox'}
                    aria-checked={action.checked === undefined ? undefined : action.checked}
                    onClick={() => { setOpen(false); action.onSelect?.() }}
                  >
                    {content}
                  </button>
                )}
              </Fragment>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}
